import React, { useEffect, useMemo, useRef, useState, } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import {
    FiArrowLeft, FiCalendar, FiCheck,
    FiCheckCircle, FiChevronDown, FiCreditCard,
    FiDollarSign, FiEdit2, FiEdit3,
    FiFileText, FiHash, FiHome, FiInfo,
    FiMapPin, FiPhone, FiPrinter, FiSend,
    FiTrash2, FiUser, FiX, FiXCircle,
} from "react-icons/fi";
import {
    Banner, CompanyStatusBanner, DetailRow, DetailSectionHeading,
    DiscardModal, DocStatusBadge, FieldInput, LineItemsTable,
    QuotationPrintTemplate, SearchableSelect, SectionHeading,
    SimpleSelect, T, btnStyleDanger, btnStyleOutline, btnStylePrimary, round2,
} from "./SalesShared";
import { getCurrencySettings } from "../../_services/finance.service";
import "../../Assets/Accounting.css";


// MODULE-LEVEL CONSTANTS
const today = () => new Date().toISOString().slice(0, 10);

/** Default country → currency mapping used when a new customer is selected. */
const COUNTRY_CURRENCY_MAP = {
    Malaysia: "MYR",
    Singapore: "SGD",
    USA: "USD",
    "United States": "USD",
    China: "CNY",
    Thailand: "THB",
    Indonesia: "IDR",
    Vietnam: "VND",
    Japan: "JPY",
    Europe: "EUR",
};

/** Fallback currency list — overwritten by API response on mount. */
const DEFAULT_CURRENCIES = [
    { code: "MYR", name: "Malaysian Ringgit", rate: 1.0 },
    { code: "USD", name: "US Dollar", rate: 0.24 },
    { code: "EUR", name: "Euro", rate: 0.20 },
    { code: "GBP", name: "British Pound", rate: 0.17 },
    { code: "SGD", name: "Singapore Dollar", rate: 0.32 },
    { code: "CNY", name: "Chinese Yuan", rate: 1.71 },
    { code: "JPY", name: "Japanese Yen", rate: 26.19 },
    { code: "THB", name: "Thai Baht", rate: 7.86 },
    { code: "IDR", name: "Indonesian Rupiah", rate: 3333.33 },
    { code: "VND", name: "Vietnamese Dong", rate: 5476.19 },
];

const BASE_EMPTY_DRAFT = {
    date: today(),
    customer: "",
    customerAddress: "",
    shippingAddress: "",
    contactPerson: "",
    paymentTerms: "",
    currency: "MYR",
    status: "Draft",
    notes: "",
    items: [],
};


// PERMISSION HELPER
function getPermissions(status) {
    const canEdit = status === "Draft" || status === "Rejected" || status === "Approved";
    return {
        canEdit,
        canEditStatusOnly: false,
        canPrint: !["Cancelled", "Void"].includes(status),
        canDelete: status === "Draft" || status === "Rejected",
        viewOnly: !canEdit,
        isArchive: false,
    };
}


// SUCCESS MESSAGE BUILDER
function buildSuccessMessage({ isAdd, docType, id, status }) {
    if (status === "Pending for Approval") {
        return isAdd
            ? `${docType} ${id} submitted for approval.`
            : `${docType} submitted for approval.`;
    }
    if (status === "Draft") {
        return isAdd
            ? `${docType} ${id} saved as Draft.`
            : `${docType} saved as Draft.`;
    }
    if (status === "Cancelled") return `${docType} cancelled.`;
    const verb = isAdd ? "created" : "updated";
    return isAdd
        ? `${docType} ${id} ${verb} successfully.`
        : `${docType} ${verb} successfully.`;
}


// INLINE STYLE HELPERS
const selectStyle = (hasError, disabled) => ({
    width: "100%", height: 36, padding: "0 28px 0 11px",
    fontSize: 13, color: T.textPrimary,
    border: `1.5px solid ${hasError ? T.danger : T.border}`,
    borderRadius: T.radiusSm, outline: "none",
    appearance: "none", WebkitAppearance: "none",
    background: disabled ? T.bg : "#fff",
    boxSizing: "border-box",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "border-color 0.15s",
});

/** Style for the tab switch buttons (Details / Preview). */
const tabBtnStyle = (active) => ({
    padding: "6px 16px", borderRadius: 6, fontSize: 13, fontWeight: 600, border: "none",
    background: active ? "#fff" : "transparent",
    color: active ? T.accent : T.textMuted,
    boxShadow: active ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
    cursor: "pointer", transition: "all 0.15s",
});


// MAIN COMPONENT
export default function SalesDocDetails({
    // Identity
    docType = "Document",
    basePath = "/sales",
    // API
    fetchById,
    updateItem,
    createItem,
    deleteItem,
    // Customer data loaders
    fetchCustomerNames,
    fetchContactPersons,
    fetchCustomerDetails,
    fetchCompanyProfile,
    // Config
    taxRate = 0,
    statuses = ["Draft", "Pending for Approval", "Sent", "Accepted", "Rejected"],
    emptyDraft = {},
    extraFields = null,
    extraFieldProps = {},
    hideCurrency = false,
    hidePaymentTerms = true,
    addressLabel = "Customer Address",
    addressField = "customerAddress",
    // Draft seeding
    fillDraft = null,
    initialDraft = null,
    // Callbacks
    onIsAddChange = null,
    onBeforeSave = null,
    onCustomerChange = null,
    onDependentFieldChange = null,
    // Presentation
    headerSlot = null,
    lineItemProps = {},
    forceNegativeTotals = false,
    totalLabel = "Grand Total (RM)",
    skipApproval = false,
}) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isAdd = id === "add";
    const fromApprovals = !!searchParams.get("fromApprovals");
    const isAuthorizedApprover = searchParams.get("isAuthorized") === "true";

    // ── Notify parent when add mode changes ──────────────────────────────────
    useEffect(() => {
        if (onIsAddChange) onIsAddChange(isAdd);
    }, [isAdd, onIsAddChange]);

    // ── Derived empty draft ──────────────────────────────────────────────────
    const EMPTY = useMemo(() => ({ ...BASE_EMPTY_DRAFT, ...emptyDraft }), [emptyDraft]);

    // ── Component state ──────────────────────────────────────────────────────
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState({ ...EMPTY });
    const [edit, setEdit] = useState(isAdd || searchParams.get("mode") === "edit");
    const [loading, setLoading] = useState(!isAdd);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [banner, setBanner] = useState({ type: "", msg: "" });
    const [errors, setErrors] = useState({});
    const [customerNames, setCN] = useState([]);
    const [contactPersonOpts, setCPO] = useState([]);
    const [showDiscard, setDiscard] = useState(false);
    const [showDelConfirm, setDelConfirm] = useState(false);
    const [companyProfile, setCP] = useState(null);
    const [activeTab, setActiveTab] = useState("details");
    const [sending, setSending] = useState(false);
    const [lastSent, setLastSent] = useState(null);
    const [cpSelectOpen, setCpSelectOpen] = useState(false);
    const [currencySettings, setCurrencySettings] = useState({
        defaultCurrency: "MYR",
        currencies: DEFAULT_CURRENCIES,
    });

    const prevCustomerRef = useRef("");
    const printRef = useRef(null);
    const bannerTimer = useRef(null);

    const isInactive = !!(companyProfile && companyProfile.Status !== "Active");

    // ── Print handler ────────────────────────────────────────────────────────
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `${data?.id || docType}-${today()}`,
    });

    // ── Banner with auto-dismiss ─────────────────────────────────────────────
    const showBanner = (type, msg, ms = 4000) => {
        setBanner({ type, msg });
        clearTimeout(bannerTimer.current);
        bannerTimer.current = setTimeout(() => setBanner({ type: "", msg: "" }), ms);
    };

    // ── Effects: one-time loads ──────────────────────────────────────────────

    useEffect(() => {
        if (fetchCompanyProfile) fetchCompanyProfile().then(setCP).catch(() => { });
    }, [fetchCompanyProfile]);

    useEffect(() => {
        if (fetchCustomerNames) fetchCustomerNames().then(setCN).catch(() => { });
    }, [fetchCustomerNames]);

    // Fetch live currency rates from the API
    useEffect(() => {
        const userStr = localStorage.getItem("user");
        if (!userStr) return;
        const user = JSON.parse(userStr);
        getCurrencySettings(user.accountId)
            .then((res) => { if (res?.currencies) setCurrencySettings(res); })
            .catch(() => { });
    }, []);

    // ── Effect: load existing document or seed new one ───────────────────────
    useEffect(() => {
        if (isAdd) {
            setData({ ...EMPTY });
            if (initialDraft) {
                setDraft((prev) => ({ ...prev, ...initialDraft }));
                if (initialDraft.customer) prevCustomerRef.current = initialDraft.customer;
            }
            return;
        }

        if (!id) return;
        setLoading(true);

        fetchById(id)
            .then((d) => {
                if (!d) showBanner("danger", `${docType} "${id}" not found.`);
                setData(d);
                setDraft({
                    ...EMPTY,
                    ...d,
                    // Normalise `selected` so undefined → true, false → false
                    items: (d.items || []).map((item) => ({
                        ...item,
                        selected: item.selected !== false,
                    })),
                });
                prevCustomerRef.current = d?.customer || "";
                if (onDependentFieldChange && d?.customer && d?.contactPerson) {
                    onDependentFieldChange(d.customer, d.contactPerson);
                }
            })
            .catch(() => showBanner("danger", `Failed to load ${docType.toLowerCase()}.`))
            .finally(() => setLoading(false));
    }, [id, isAdd]); // eslint-disable-line react-hooks/exhaustive-deps
    // NOTE: fetchById and EMPTY are intentionally omitted — they are stable
    //       across renders and re-fetching on every re-render would cause loops.

    // ── Effect: react to customer field change ───────────────────────────────
    useEffect(() => {
        if (!draft.customer) {
            setCPO([]);
            prevCustomerRef.current = "";
            return;
        }

        if (onCustomerChange) onCustomerChange(draft.customer);

        if (fetchContactPersons) {
            fetchContactPersons(draft.customer)
                .then((p) => setCPO(p || []))
                .catch(() => setCPO([]));
        }

        if (!edit || !fetchCustomerDetails) return;

        fetchCustomerDetails(draft.customer)
            .then((details) => {
                const customerChanged = prevCustomerRef.current !== draft.customer;
                prevCustomerRef.current = draft.customer;
                if (!customerChanged) return;

                // Auto-detect currency from customer country
                if (details?.country && isAdd && !draft.currency) {
                    const mapped = COUNTRY_CURRENCY_MAP[details.country];
                    if (mapped) setDraft((prev) => ({ ...prev, currency: mapped }));
                }

                // Reset extra draft keys when the customer changes
                const extraReset = Object.keys(emptyDraft).reduce((acc, k) => {
                    if (k !== "customerAddress" && k !== "shippingAddress") acc[k] = "";
                    return acc;
                }, {});

                setDraft((prev) => ({
                    ...prev,
                    ...details,
                    [addressField]: details[addressField] || details.customerAddress || details.billingAddress || "",
                    shippingAddress: details.shippingAddress || "",
                    contactPerson: "",
                    phoneNumber: "",
                    paymentTerms: "",
                    items: [],
                    ...extraReset,
                }));
            })
            .catch(() => { });
    }, [draft.customer, edit]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Effect: merge fillDraft when parent provides new fill data ───────────
    useEffect(() => {
        if (!fillDraft) return;
        setDraft((prev) => ({ ...prev, ...fillDraft }));
        if (fillDraft.customer) prevCustomerRef.current = fillDraft.customer;
    }, [fillDraft]);

    // CHANGE HANDLER
    const onChange = (e) => {
        const { name, value } = e.target;

        setDraft((prev) => {
            const next = { ...prev, [name]: value };

            // Reset downstream fields when company is cleared
            if (name === "customer" && !value) {
                next.contactPerson = "";
                next.paymentTerms = "";
                next.items = [];
                Object.keys(emptyDraft).forEach((k) => { next[k] = ""; });
                if (docType === "Delivery Order") next.deliveryDate = today();
            }

            // Reset phone/terms when contact person is cleared
            if (name === "contactPerson" && !value) {
                next.phoneNumber = "";
                next.paymentTerms = "";
                if (docType === "Delivery Order") next.deliveryDate = today();
            }

            return next;
        });

        // Side-effects for contactPerson change
        if (name === "contactPerson") {
            if (onDependentFieldChange) onDependentFieldChange(draft.customer, value);

            if (value && fetchCustomerDetails && draft.customer) {
                fetchCustomerDetails(draft.customer)
                    .then((details) => {
                        setDraft((prev) => ({
                            ...prev,
                            paymentTerms: details.paymentTerms || "",
                            phoneNumber: details.phoneNumber || "",
                        }));
                    })
                    .catch(() => { });
            }
        }

        // Clear field-level error on change
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };


    // VALIDATION
    const validate = () => {
        const errs = {};
        const noPrice = lineItemProps?.noPrice;

        // Date
        if (!draft.date) errs.date = "Date is required.";

        // Extra draft fields with required rules
        const has = (key) => Object.prototype.hasOwnProperty.call(emptyDraft, key);
        if (has("validUntil") && !draft.validUntil) errs.validUntil = "Valid Until is required.";
        if (has("quotationRef") && !draft.quotationRef) errs.quotationRef = "Quotation Ref is required.";
        if (has("doId") && !draft.doId) errs.doId = "Delivery Order Ref is required.";
        if (has("invoiceId") && !draft.invoiceId) errs.invoiceId = "Invoice Ref is required.";
        if (has("reason") && !draft.reason) errs.reason = "Reason is required.";

        // Delivery date (present on several doc types)
        if (draft.deliveryDate !== undefined && !draft.deliveryDate) {
            errs.deliveryDate = "Delivery Date is required.";
        }

        // Customer + contact person
        if (!draft.customer) errs.customer = "Customer is required.";
        if (!draft.contactPerson && contactPersonOpts.length > 0) {
            errs.contactPerson = "Contact person is required.";
        }

        // Line items
        const itemsToValidate = lineItemProps.selectable
            ? draft.items.filter((item) => item.selected)
            : draft.items;

        if (!itemsToValidate.length) {
            errs.items = lineItemProps.selectable
                ? "At least one line item must be selected."
                : "At least one line item must be added.";
        } else {
            itemsToValidate.forEach((row, i) => {
                const rowLabel = i + 1;
                if (!row.itemCode?.trim()) errs[`items.${i}.itemCode`] = `Row ${rowLabel}: Item Code is required.`;
                if (!row.description?.trim()) errs[`items.${i}.description`] = `Row ${rowLabel}: Description is required.`;
                if (!row.unit?.trim()) errs[`items.${i}.unit`] = `Row ${rowLabel}: Unit is required.`;
                const qty = Number(row.qty);
                if (!qty || qty < 1) errs[`items.${i}.qty`] = `Row ${rowLabel}: Quantity must be greater than 0.`;
                if (!noPrice && (Number(row.unitPrice) == null || Number(row.unitPrice) <= 0)) {
                    errs[`items.${i}.unitPrice`] = `Row ${rowLabel}: Unit Price must be greater than 0.`;
                }
            });
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };


    // SAVE
    const save = async (overrideStatus = null) => {
        if (!validate()) {
            showBanner("danger", "Please fix the errors before saving.");
            return;
        }
        if (isInactive) {
            showBanner("danger", "Action restricted. Your company status is currently Inactive.");
            return;
        }

        setSaving(true);
        try {
            let finalDraft = { ...draft, items: (draft.items || []).map((item) => ({ ...item })) };
            if (typeof overrideStatus === "string" && overrideStatus) {
                finalDraft.status = overrideStatus;
            }
            if (onBeforeSave) finalDraft = onBeforeSave(finalDraft);

            // Compute grand total
            const selectedItems = (finalDraft.items || []).filter((item) => item.selected !== false);
            const subtotalAbs = selectedItems.reduce(
                (s, r) => s + round2((Number(r.qty) || 0) * (Number(r.unitPrice) || 0)), 0
            );
            const taxAbs = round2(subtotalAbs * (taxRate / 100));
            let grandTotalCalc = round2(subtotalAbs + taxAbs);
            if (forceNegativeTotals) grandTotalCalc = -Math.abs(grandTotalCalc);

            // Build payload
            const payload = {
                date: finalDraft.date,
                customer: finalDraft.customer,
                quotationRef: finalDraft.quotationRef || null,
                deliveryDate: finalDraft.deliveryDate || null,
                deliveryOrderRef: finalDraft.deliveryOrderRef || null,
                invoiceRef: finalDraft.invoiceRef || null,
                phoneNumber: finalDraft.phoneNumber || null,
                [addressField]: finalDraft[addressField] || null,
                shippingAddress: finalDraft.shippingAddress || null,
                contactPerson: finalDraft.contactPerson || null,
                paymentTerms: finalDraft.paymentTerms || null,
                currency: finalDraft.currency || "MYR",
                status: finalDraft.status || "Draft",
                notes: finalDraft.notes || null,
                items: selectedItems,
                grandTotal: grandTotalCalc,
            };

            // Merge extra draft keys
            Object.keys(emptyDraft).forEach((k) => { payload[k] = finalDraft[k] || null; });

            if (isAdd) {
                const res = await createItem(payload);
                const newId = res.id || res.quotation_id || res.do_id || res.invoice_id || res.cn_id;
                if (!res || !newId) throw new Error("No ID returned from server.");
                showBanner("success", buildSuccessMessage({ isAdd: true, docType, id: newId, status: payload.status }));
                if (payload.status === "Pending for Approval") {
                    window.dispatchEvent(new CustomEvent("refreshPendingApprovalCounts"));
                }
                setTimeout(() => navigate(basePath), 1200);
            } else {
                await updateItem(id, payload);
                const savedItems = selectedItems.map((item) => ({ ...item, selected: true }));
                const updatedData = { ...data, ...payload, items: savedItems };
                setData(updatedData);
                setDraft((prev) => ({ ...prev, items: savedItems }));
                setEdit(false);
                showBanner("success", buildSuccessMessage({ isAdd: false, docType, id, status: payload.status }));
                if (payload.status === "Pending for Approval" || data?.status === "Pending for Approval") {
                    window.dispatchEvent(new CustomEvent("refreshPendingApprovalCounts"));
                }
            }
        } catch (err) {
            console.error("Save error:", err);
            showBanner("danger", `Failed to save ${docType.toLowerCase()}. ${err?.message ? `(${err.message})` : ""}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (isInactive) {
            showBanner("danger", "Action restricted. Your company status is currently Inactive.");
            return;
        }
        setDeleting(true);
        try {
            if (deleteItem) await deleteItem(id);
            if (data?.status === "Pending for Approval") {
                window.dispatchEvent(new CustomEvent("refreshPendingApprovalCounts"));
            }
            navigate(basePath);
        } catch {
            showBanner("danger", `Failed to delete ${docType.toLowerCase()}.`);
        } finally {
            setDeleting(false);
            setDelConfirm(false);
        }
    };


    // SEND (stub — replace with real email API when ready)
    const handleSend = () => {
        if (isInactive) {
            showBanner("danger", "Action restricted. Your company status is currently Inactive.");
            return;
        }
        if (!window.confirm(`Are you sure you want to send this ${docType} to ${data.customer}?`)) return;
        setSending(true);
        setTimeout(() => {
            setSending(false);
            setLastSent(Date.now());
            showBanner("success", `${docType} sent successfully to ${data.customer}.`);
        }, 1500);
    };

    // DIRTY CHECK
    const isDirty = () => {
        const base = isAdd ? EMPTY : data;
        if (!base) return false;
        const keys = [
            "date", "validUntil", "customer", addressField,
            "contactPerson", "paymentTerms", "currency", "status", "notes", "items",
            ...Object.keys(emptyDraft),
        ];
        const normalise = (items) =>
            (items || []).map((item) => ({ ...item, selected: item.selected !== false }));
        if (JSON.stringify(normalise(draft.items)) !== JSON.stringify(normalise(base.items))) return true;
        return keys.some((k) => k !== "items" && JSON.stringify(draft[k]) !== JSON.stringify(base[k]));
    };


    // CANCEL EDIT
    const cancelEdit = () => {
        if (isDirty()) { setDiscard(true); return; }
        if (isAdd) { navigate(basePath); return; }

        const originalItems = (data?.items || []).map((item) => ({ ...item, selected: item.selected !== false }));
        setDraft({ ...EMPTY, ...data, items: originalItems });
        prevCustomerRef.current = data?.customer || "";
        setEdit(false);
        setErrors({});
    };

    // MEMOISED TOTALS
    const { currentItems } = useMemo(() => {
        const items = edit ? draft.items : (data?.items ?? []);
        return { currentItems: items };
    }, [edit, draft.items, data?.items]);

    // DERIVED DISPLAY VALUES
    const perms = data
        ? getPermissions(data.status)
        : { canEdit: true, canPrint: true, viewOnly: false, isArchive: false };

    const initials = (() => {
        const name = (edit ? draft : data)?.customer || docType;
        return name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
    })();

    // ── Item field error map for LineItemsTable ──────────────────────────────
    const itemFieldErrors = Object.keys(errors)
        .filter((k) => k.startsWith("items."))
        .reduce((obj, k) => {
            const m = k.match(/^items\.(\d+)\.(.*)$/);
            if (m) {
                if (!obj[m[1]]) obj[m[1]] = {};
                obj[m[1]][m[2]] = errors[k];
            }
            return obj;
        }, {});

    // ── Approve / Reject (from approvals view) ───────────────────────────────
    const handleApprove = async () => {
        if (!window.confirm("Approve this document?")) return;
        try {
            await updateItem(data.id, { status: "Approved" });
            showBanner("success", `Document ${data.id} approved successfully.`);
            window.dispatchEvent(new CustomEvent("refreshPendingApprovalCounts"));
            fetchById(id).then((d) => { setData(d); setDraft({ ...EMPTY, ...d }); });
        } catch (err) {
            showBanner("danger", `Failed to approve document ${data.id}.`);
        }
    };

    const handleReject = async () => {
        const reason = window.prompt("Please provide a reason for rejection:");
        if (reason === null) return;
        if (!reason.trim()) { alert("Rejection reason is required."); return; }
        try {
            await updateItem(data.id, { status: "Rejected", rejectionReason: reason });
            showBanner("success", `Document ${data.id} rejected successfully.`);
            window.dispatchEvent(new CustomEvent("refreshPendingApprovalCounts"));
            fetchById(id).then((d) => { setData(d); setDraft({ ...EMPTY, ...d }); });
        } catch (err) {
            showBanner("danger", `Failed to reject document ${data.id}.`);
        }
    };

    // LOADING / NOT FOUND STATES
    if (loading)
        return <div style={{ padding: 40, color: T.textMuted, fontSize: 14 }}>Loading {docType.toLowerCase()}…</div>;

    if (!isAdd && !loading && !data)
        return (
            <div style={{ padding: 40 }}>
                <Banner type="danger" msg={`${docType} "${id}" was not found.`} onClose={() => { }} />
            </div>
        );

    // RENDER
    return (
        <div style={{ padding: "20px 0", width: "100%", boxSizing: "border-box" }}>
            <Banner type={banner.type} msg={banner.msg} onClose={() => setBanner({ type: "", msg: "" })} />
            <CompanyStatusBanner isInactive={isInactive} onNavigate={() => navigate("/contacts/company")} />
            {headerSlot}

            {/* ── Page header bar ────────────────────────────────────────── */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "nowrap" }}>
                {/* Back button */}
                {!isAdd && (
                    <button
                        onClick={() => {
                            if (fromApprovals) navigate("/accounting-approvals/sales-approvals");
                            else if (!edit) navigate(basePath);
                        }}
                        disabled={edit}
                        title={edit
                            ? "Save or cancel your changes before going back"
                            : fromApprovals ? "Back to Approvals" : `Back to ${docType} list`
                        }
                        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, height: 36, padding: "0 14px", boxSizing: "border-box", borderRadius: T.radiusSm, border: `1.5px solid ${T.border}`, background: edit ? T.bg : "#fff", color: edit ? T.textLight : T.textMuted, fontSize: 13, fontWeight: 500, cursor: edit ? "not-allowed" : "pointer", opacity: edit ? 0.5 : 1, transition: "all 0.15s" }}
                    >
                        <FiArrowLeft size={14} /> {fromApprovals ? "Back to Approvals" : "Back"}
                    </button>
                )}

                {/* Title */}
                <h4 style={{ margin: 0, fontWeight: 700, color: T.textPrimary, fontSize: 18 }}>
                    {isAdd ? `Create ${docType}` : `${docType} Details`}
                </h4>

                {/* Tab switcher */}
                <div style={{ display: "flex", gap: 4, background: T.bg, padding: 3, borderRadius: T.radiusSm, marginLeft: 4 }}>
                    <button onClick={() => setActiveTab("details")} style={tabBtnStyle(activeTab === "details")}>Details</button>
                    <button onClick={() => setActiveTab("preview")} style={tabBtnStyle(activeTab === "preview")}>Preview {docType}</button>
                </div>

                {/* Create DO from Invoice shortcut */}
                {data?.status === "Approved" && docType === "Invoice" && (data?.soRef || data?.so_ref) && (
                    <button
                        onClick={() => navigate(`/sales/delivery-orders/add?fromSO=${data.soRef || data.so_ref}&fromInvoice=true`)}
                        style={btnStyleOutline}
                        title="Create a Delivery Order from this Invoice's Sales Order"
                    >
                        <FiFileText size={13} /> Create DO
                    </button>
                )}

                {/* View-mode action buttons */}
                {!isAdd && !edit && perms.canPrint && (
                    <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                        {data?.status === "Approved" && (
                            <button
                                onClick={handleSend}
                                disabled={sending || (lastSent && Date.now() - lastSent < 60000) || isInactive}
                                style={{ ...btnStyleOutline, opacity: sending || isInactive ? 0.6 : 1 }}
                                title={lastSent && Date.now() - lastSent < 60000 ? "Please wait before sending again" : "Send to customer email"}
                            >
                                <FiSend size={13} /> {sending ? "Sending..." : "Send Document"}
                            </button>
                        )}
                        {!fromApprovals && (
                            <button onClick={handlePrint} style={btnStyleOutline}>
                                <FiPrinter size={13} /> Print / Save PDF
                            </button>
                        )}
                        {data?.status === "Approved" && (
                            <button
                                onClick={() => {
                                    if (window.confirm(`Cancel this ${docType}? The document will be marked as Cancelled and cannot be edited further.`)) {
                                        save("Cancelled");
                                    }
                                }}
                                disabled={isInactive}
                                style={{ ...btnStyleDanger, opacity: isInactive ? 0.7 : 1, cursor: isInactive ? "not-allowed" : "pointer" }}
                            >
                                <FiX size={13} /> Cancel
                            </button>
                        )}
                        {/* Approve / Reject (approvals flow) */}
                        {fromApprovals && isAuthorizedApprover && data?.status === "Pending for Approval" && (
                            <>
                                <button onClick={handleApprove} style={{ ...btnStylePrimary, background: T.success }}>
                                    <FiCheckCircle size={13} /> Approve
                                </button>
                                <button onClick={handleReject} style={{ ...btnStylePrimary, background: "#fff", color: T.danger, border: `1.5px solid ${T.danger}` }}>
                                    <FiXCircle size={13} /> Reject
                                </button>
                            </>
                        )}
                        {perms.canEdit && (
                            <button onClick={() => setEdit(true)} style={btnStylePrimary}>
                                <FiEdit3 size={13} /> Edit
                            </button>
                        )}
                    </div>
                )}

                {/* Edit-mode save buttons (existing doc) */}
                {edit && !isAdd && (
                    <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                        <button onClick={cancelEdit} disabled={saving} style={btnStyleOutline}><FiX size={13} /> Cancel</button>
                        <button
                            onClick={() => save("Draft")}
                            disabled={saving || isInactive}
                            style={{ ...btnStylePrimary, background: isInactive ? "#94a3b8" : saving ? "#93c5fd" : T.accent, cursor: isInactive ? "not-allowed" : "pointer", opacity: isInactive ? 0.7 : 1 }}
                        >
                            <FiCheck size={13} /> Save as Draft
                        </button>
                        {skipApproval ? (
                            <button
                                onClick={() => save("Approved")}
                                disabled={saving || isInactive}
                                style={{ ...btnStylePrimary, background: isInactive ? "#94a3b8" : saving ? "#a7f3d0" : T.success, cursor: isInactive ? "not-allowed" : "pointer", opacity: isInactive ? 0.7 : 1 }}
                            >
                                <FiCheck size={13} /> Save & Approve
                            </button>
                        ) : (
                            <button
                                onClick={() => save("Pending for Approval")}
                                disabled={saving || isInactive}
                                style={{ ...btnStylePrimary, background: isInactive ? "#94a3b8" : saving ? "#a7f3d0" : T.success, cursor: isInactive ? "not-allowed" : "pointer", opacity: isInactive ? 0.7 : 1 }}
                            >
                                <FiCheck size={13} /> Submit for Approval
                            </button>
                        )}
                    </div>
                )}

                {/* Archive / delete button */}
                {!isAdd && !edit && perms.isArchive && (
                    <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                        <button
                            onClick={() => setDelConfirm(true)}
                            disabled={isInactive}
                            style={{ ...btnStyleDanger, background: isInactive ? "#94a3b8" : T.danger, cursor: isInactive ? "not-allowed" : "pointer", opacity: isInactive ? 0.7 : 1 }}
                        >
                            <FiTrash2 size={13} /> Delete
                        </button>
                    </div>
                )}
            </div>

            {/* ═══════════════════════════════════════════════════════════════
                PREVIEW TAB
            ════════════════════════════════════════════════════════════════ */}
            {activeTab === "preview" && (
                <div style={{ background: T.cardBg, borderRadius: T.radius, border: `1px solid ${T.border}`, boxShadow: T.shadow, padding: "24px", overflow: "auto" }}>
                    {isAdd && (
                        <div style={{ marginBottom: 12, padding: "8px 14px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: T.radiusSm, fontSize: 13, color: "#92400e", fontWeight: 500 }}>
                            Preview mode — fill in the form to see a live preview. Print / Save PDF is available after creating the {docType}.
                        </div>
                    )}
                    <QuotationPrintTemplate
                        ref={printRef}
                        data={{
                            ...(edit || isAdd ? draft : data || draft),
                            phoneNumber: (edit || isAdd ? draft : data || draft)?.phoneNumber || "",
                            items: (edit || isAdd ? draft : data || draft)?.items?.filter((item) => item.selected !== false) || [],
                        }}
                        companyProfile={companyProfile}
                        docType={docType}
                        taxRate={taxRate}
                        forceNegativeTotals={forceNegativeTotals}
                    />
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════
                DETAILS TAB
            ════════════════════════════════════════════════════════════════ */}
            {activeTab === "details" && (
                <>
                    {/* Hidden print target */}
                    <div style={{ display: "none" }}>
                        <QuotationPrintTemplate
                            ref={printRef}
                            data={{
                                ...(data || draft),
                                phoneNumber: (edit || isAdd ? draft : data || draft)?.phoneNumber || "",
                                items: (data || draft)?.items?.filter((item) => item.selected !== false) || [],
                            }}
                            companyProfile={companyProfile}
                            docType={docType}
                            taxRate={taxRate}
                        />
                    </div>

                    {/* Document profile strip (view mode only) */}
                    {!isAdd && data && !edit && (
                        <div style={{ background: T.cardBg, borderRadius: T.radius, border: `1px solid ${T.border}`, boxShadow: T.shadow, padding: "22px 28px", marginBottom: 16 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>
                                {docType.toUpperCase()} PROFILE
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
                                <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#eff6ff", color: T.accent, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700 }}>
                                    {initials}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 20, fontWeight: 700, color: T.textPrimary }}>{data.customer}</div>
                                    <div style={{ fontSize: 13, color: T.textMuted, marginTop: 2 }}>{data.contactPerson}</div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10, flexWrap: "wrap" }}>
                                        <span style={{ fontSize: 13, color: T.textMuted }}>ID: <strong style={{ color: T.textPrimary }}>{data.id}</strong></span>
                                        {data.date && <span style={{ fontSize: 13, color: T.textMuted }}>Date: <strong style={{ color: T.textPrimary }}>{data.date}</strong></span>}
                                        {data.validUntil && <span style={{ fontSize: 13, color: T.textMuted }}>Valid Until: <strong style={{ color: T.textPrimary }}>{data.validUntil}</strong></span>}
                                        <DocStatusBadge value={data.status} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Main form / view card ─────────────────────────── */}
                    <div style={{ background: T.cardBg, borderRadius: T.radius, border: `1px solid ${T.border}`, boxShadow: T.shadow, overflow: "visible", marginBottom: 16 }}>

                        {/* ── EDIT MODE ───────────────────────────────────── */}
                        {edit ? (
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", borderBottom: `1px solid ${T.border}` }}>

                                {/* Customer Info column */}
                                <div style={{ padding: "22px 24px", borderRight: `1px solid ${T.border}` }}>
                                    <SectionHeading icon={FiUser} title="Customer Info" />

                                    <SearchableSelect
                                        label="Company Name"
                                        name="customer"
                                        value={draft.customer}
                                        onChange={onChange}
                                        options={customerNames}
                                        placeholder="Type or select customer..."
                                        required
                                        error={errors.customer}
                                    />

                                    {/* Contact Person — dropdown if options available, free-text otherwise */}
                                    <div style={{ marginBottom: 14 }}>
                                        <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 5 }}>
                                            Contact Person {contactPersonOpts.length > 0 && <span style={{ color: T.danger }}>*</span>}
                                        </label>
                                        {!draft.customer ? (
                                            <input
                                                type="text"
                                                disabled
                                                placeholder="Select customer first..."
                                                style={{ ...selectStyle(false, true), height: 36, padding: "0 11px", fontSize: 13, display: "block" }}
                                            />
                                        ) : contactPersonOpts.length > 0 ? (
                                            <div style={{ position: "relative" }}>
                                                <select
                                                    name="contactPerson"
                                                    value={draft.contactPerson}
                                                    onChange={onChange}
                                                    onFocus={() => setCpSelectOpen(true)}
                                                    onBlur={() => setCpSelectOpen(false)}
                                                    style={{ ...selectStyle(!!errors.contactPerson, false), appearance: "none", WebkitAppearance: "none", paddingRight: 30 }}
                                                >
                                                    <option value="">Select Contact Person...</option>
                                                    {contactPersonOpts.map((n) => <option key={n}>{n}</option>)}
                                                </select>
                                                <span style={{ position: "absolute", right: 9, top: "50%", transform: `translateY(-50%) rotate(${cpSelectOpen ? 180 : 0}deg)`, pointerEvents: "none", transition: "transform 0.2s ease", display: "inline-flex", alignItems: "center", color: T.textLight }}>
                                                    <FiChevronDown size={14} />
                                                </span>
                                            </div>
                                        ) : (
                                            <input
                                                type="text"
                                                name="contactPerson"
                                                value={draft.contactPerson}
                                                onChange={onChange}
                                                placeholder="Type contact person name..."
                                                style={{ width: "100%", height: 36, padding: "0 11px", fontSize: 13, color: T.textPrimary, border: `1.5px solid ${errors.contactPerson ? T.danger : T.border}`, borderRadius: T.radiusSm, outline: "none", background: "#fff", boxSizing: "border-box", display: "block" }}
                                            />
                                        )}
                                        {errors.contactPerson && <div style={{ fontSize: 11, color: T.danger, marginTop: 4 }}>{errors.contactPerson}</div>}
                                    </div>

                                    <FieldInput label="Phone Number" name="phoneNumber" value={draft.phoneNumber || ""} readOnly placeholder="Auto-filled from customer" />
                                    <FieldInput label={addressLabel} name={addressField} as="textarea" rows={3} value={draft[addressField] || ""} onChange={onChange} placeholder={`Auto-filled from ${addressLabel.toLowerCase()}`} readOnly />
                                    {addressField !== "shippingAddress" && (
                                        <FieldInput label="Shipping Address" name="shippingAddress" as="textarea" rows={3} value={draft.shippingAddress || ""} onChange={onChange} placeholder="Auto-filled from customer shipping address" readOnly />
                                    )}
                                </div>

                                {/* Document Info column */}
                                <div style={{ padding: "22px 24px", borderRight: `1px solid ${T.border}` }}>
                                    <SectionHeading icon={FiCalendar} title="Document Info" />
                                    <div style={{ marginBottom: 14 }}>
                                        <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 5 }}>
                                            Date <span style={{ color: T.danger }}>*</span>
                                        </label>
                                        <input
                                            type="date"
                                            name="date"
                                            value={draft.date}
                                            onChange={onChange}
                                            min={edit ? today() : undefined}
                                            style={{ width: "100%", height: 36, padding: "0 11px", fontSize: 13, color: T.textPrimary, border: `1.5px solid ${errors.date ? T.danger : T.border}`, borderRadius: T.radiusSm, outline: "none", background: "#fff", boxSizing: "border-box", display: "block" }}
                                        />
                                        {errors.date && <div style={{ fontSize: 11, color: T.danger, marginTop: 4 }}>{errors.date}</div>}
                                    </div>
                                    {/* Extra fields slot (left column) */}
                                    {extraFields?.left?.({ draft, onChange, errors, edit, dateMin: edit ? today() : undefined, ...extraFieldProps })}
                                </div>

                                {/* Terms & Status column */}
                                <div style={{ padding: "22px 24px" }}>
                                    <SectionHeading icon={FiDollarSign} title="Terms & Status" />
                                    {!hidePaymentTerms && draft.customer && (
                                        <div style={{ marginBottom: 14 }}>
                                            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 5 }}>
                                                Payment Terms
                                                {draft.customer && !draft.paymentTerms && (
                                                    <span style={{ color: T.textLight, fontWeight: 400, marginLeft: 6 }}>(Select customer first)</span>
                                                )}
                                            </label>
                                            <input
                                                type="text"
                                                value={draft.paymentTerms || ""}
                                                readOnly
                                                placeholder="Auto-filled from customer"
                                                style={{ width: "100%", height: 36, padding: "0 11px", fontSize: 13, color: draft.paymentTerms ? T.textPrimary : T.textLight, background: T.bg, border: `1.5px solid ${T.border}`, borderRadius: T.radiusSm, outline: "none", boxSizing: "border-box", cursor: "default", display: "block" }}
                                            />
                                        </div>
                                    )}
                                    {!hideCurrency && (
                                        <SimpleSelect
                                            label="Currency"
                                            name="currency"
                                            value={draft.currency}
                                            options={currencySettings.currencies.map((c) => ({ value: c.code, label: `${c.code} - ${c.name}` }))}
                                            onChange={onChange}
                                            error={errors.currency}
                                        />
                                    )}
                                    <div style={{ marginBottom: 14 }}>
                                        <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 5 }}>Status</label>
                                        <div style={{ height: 36, display: "flex", alignItems: "center" }}>
                                            <DocStatusBadge value={draft.status} />
                                        </div>
                                    </div>
                                    <FieldInput label="Notes / Remarks" name="notes" as="textarea" rows={3} value={draft.notes || ""} onChange={onChange} placeholder="Additional notes..." />
                                    {/* Extra fields slot (right column) */}
                                    {extraFields?.right?.({ draft, onChange, errors, edit, ...extraFieldProps })}
                                </div>
                            </div>

                        ) : (
                            /* ── VIEW MODE ──────────────────────────────────── */
                            data && (
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                                    {/* Left: document info */}
                                    <div style={{ padding: "24px 28px", borderRight: `1px solid ${T.border}` }}>
                                        <DetailSectionHeading icon={FiFileText} title="Document Information" />
                                        <DetailRow icon={FiHash} label="Document No."><span style={{ color: T.textPrimary }}>{data.id}</span></DetailRow>
                                        <DetailRow icon={FiCalendar} label="Date">{data.date}</DetailRow>
                                        {extraFields?.left?.({ draft: data, onChange, errors, edit, ...extraFieldProps })}
                                        {!hidePaymentTerms && <DetailRow icon={FiCreditCard} label="Payment Terms">{data.paymentTerms}</DetailRow>}
                                        {!hideCurrency && <DetailRow icon={FiDollarSign} label="Currency">{data.currency}</DetailRow>}
                                        {data.notes && (
                                            <DetailRow icon={FiEdit2} label="Notes / Remarks">
                                                <div style={{ marginTop: 2 }}>
                                                    {data.notes.split("\n").map((line, i) => (
                                                        <p key={i} style={{ margin: "0 0 8px 0", minHeight: line ? 0 : "1em", color: T.textPrimary }}>{line}</p>
                                                    ))}
                                                </div>
                                            </DetailRow>
                                        )}
                                    </div>

                                    {/* Right: customer & status */}
                                    <div style={{ padding: "24px 28px" }}>
                                        <DetailSectionHeading icon={FiUser} title="Customer & Status" />
                                        <DetailRow icon={FiHome} label="Company Name">{data.customer}</DetailRow>
                                        <DetailRow icon={FiUser} label="Contact Person">{data.contactPerson}</DetailRow>
                                        <DetailRow icon={FiPhone} label="Phone Number">{data.phoneNumber || "—"}</DetailRow>
                                        <DetailRow icon={FiMapPin} label={addressLabel}>{data[addressField]}</DetailRow>
                                        <DetailRow icon={FiMapPin} label="Shipping Address">{data.shippingAddress || "—"}</DetailRow>
                                        <DetailRow icon={FiCheckCircle} label="Status">
                                            <DocStatusBadge value={data.status} />
                                        </DetailRow>
                                        {data.createdAt && (
                                            <div style={{ marginTop: 16, padding: "12px 14px", background: T.bg, borderRadius: T.radiusSm, fontSize: 12, color: T.textMuted }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 5, fontWeight: 700, color: T.textPrimary }}>
                                                    <FiInfo size={11} /> Metadata
                                                </div>
                                                <div>Created: {new Date(data.createdAt).toLocaleString()}</div>
                                                {data.updatedAt && <div style={{ marginTop: 2 }}>Updated: {new Date(data.updatedAt).toLocaleString()}</div>}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        )}
                    </div>

                    {/* ── Line Items card ───────────────────────────────── */}
                    <div style={{ background: T.cardBg, borderRadius: T.radius, border: `1px solid ${T.border}`, boxShadow: T.shadow, overflow: "visible", marginBottom: 16 }}>
                        <div style={{ padding: "18px 24px 4px" }}>
                            <SectionHeading icon={FiFileText} title="Line Items" />
                        </div>
                        <div style={{ padding: "0 24px 22px" }}>
                            <LineItemsTable
                                items={currentItems}
                                docType={docType}
                                onChange={(newItems) => setDraft((prev) => ({ ...prev, items: newItems }))}
                                readOnly={!edit || !!lineItemProps.readOnly}
                                taxRate={taxRate}
                                error={errors.items}
                                selectable={edit ? lineItemProps.selectable : false}
                                totalLabel={totalLabel}
                                forceNegativeTotals={forceNegativeTotals}
                                noPrice={lineItemProps.noPrice}
                                showRestock={lineItemProps.showRestock}
                                searchable={lineItemProps.searchable}
                                searchSource={lineItemProps.searchSource}
                                currencySettings={currencySettings}
                                docCurrency={draft.currency}
                                itemFieldErrors={itemFieldErrors}
                            />
                        </div>
                    </div>

                    {/* ── Create mode bottom action bar ────────────────── */}
                    {isAdd && (
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
                            <button onClick={cancelEdit} disabled={saving} style={btnStyleOutline}>
                                <FiX size={13} /> Cancel
                            </button>
                            <button
                                onClick={() => save("Draft")}
                                disabled={saving || isInactive}
                                style={{ ...btnStylePrimary, background: isInactive ? "#94a3b8" : saving ? "#93c5fd" : T.accent, cursor: isInactive ? "not-allowed" : "pointer", opacity: isInactive ? 0.7 : 1 }}
                            >
                                <FiCheck size={13} /> Save as Draft
                            </button>
                            {skipApproval ? (
                                <button
                                    onClick={() => save("Approved")}
                                    disabled={saving || isInactive}
                                    style={{ ...btnStylePrimary, background: isInactive ? "#94a3b8" : saving ? "#a7f3d0" : T.success, cursor: isInactive ? "not-allowed" : "pointer", opacity: isInactive ? 0.7 : 1 }}
                                >
                                    <FiCheck size={13} /> Save & Approve
                                </button>
                            ) : (
                                <button
                                    onClick={() => save("Pending for Approval")}
                                    disabled={saving || isInactive}
                                    style={{ ...btnStylePrimary, background: isInactive ? "#94a3b8" : saving ? "#a7f3d0" : T.success, cursor: isInactive ? "not-allowed" : "pointer", opacity: isInactive ? 0.7 : 1 }}
                                >
                                    <FiCheck size={13} /> Submit for Approval
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* ─────────────── MODALS ─────────────── */}

            {/* Discard unsaved changes */}
            <DiscardModal
                open={showDiscard}
                onConfirm={() => {
                    setDiscard(false);
                    if (isAdd) {
                        navigate(basePath);
                    } else {
                        const originalItems = (data?.items || []).map((item) => ({ ...item, selected: item.selected !== false }));
                        setDraft({ ...EMPTY, ...data, items: originalItems });
                        prevCustomerRef.current = data?.customer || "";
                        setEdit(false);
                        setErrors({});
                    }
                }}
                onCancel={() => setDiscard(false)}
            />

            {/* Delete confirmation */}
            {showDelConfirm && (
                <div
                    style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center" }}
                    onClick={() => setDelConfirm(false)}
                >
                    <div
                        style={{ background: "#fff", borderRadius: T.radius, padding: "28px 28px 22px", boxShadow: "0 8px 32px rgba(0,0,0,0.18)", width: 360, maxWidth: "90vw" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ fontSize: 15, fontWeight: 700, color: T.textPrimary, marginBottom: 10 }}>Delete {docType}?</div>
                        <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 22 }}>
                            This action cannot be undone. <strong>{data?.id}</strong> will be permanently removed.
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                            <button onClick={() => setDelConfirm(false)} style={btnStyleOutline}>Cancel</button>
                            <button
                                onClick={handleDelete}
                                disabled={deleting}
                                style={{ ...btnStyleDanger, opacity: deleting ? 0.7 : 1 }}
                            >
                                {deleting ? "Deleting…" : "Yes, Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}